import { useState, useEffect } from 'react'
import Select from 'react-select'
import { useLocation } from '../../contexts/LocationContext'
import { useLocationHierarchy } from '../../hooks/useLocationHierarchy'
import { useConfig } from '../../contexts/ConfigContext'

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    fontSize: '14px',
    borderColor: state.isFocused ? '#0ea5e9' : '#d4d4d4',
    boxShadow: state.isFocused ? '0 0 0 1px #0ea5e9' : 'none',
    '&:hover': {
      borderColor: '#a3a3a3',
    },
  }),
  menu: base => ({
    ...base,
    zIndex: 9999,
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: state.isSelected
      ? '#0ea5e9'
      : state.isFocused
        ? '#f0f9ff'
        : 'white',
    color: state.isSelected ? 'white' : '#171717',
  }),
  placeholder: base => ({
    ...base,
    fontSize: '14px',
    color: '#a3a3a3',
  }),
  singleValue: base => ({
    ...base,
    fontSize: '14px',
    color: '#171717',
  }),
}

function StickyLocationSelector() {
  const [isVisible, setIsVisible] = useState(false)

  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    setDistrict,
    setSubcounty,
    setParish,
    setVillage,
    setAllDistricts,
    reset,
    isViewingAllDistricts,
    isSingleDistrictMode,
    focusedDistrict,
  } = useLocation()

  const {
    availableDistricts,
    availableSubcounties,
    availableParishes,
    availableVillages,
    selectedDistrictData,
    selectedSubcountyData,
    selectedParishData,
  } = useLocationHierarchy()

  const { getLocationDepthOrder } = useConfig()

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky selector when scrolled past 200px
      setIsVisible(window.scrollY > 200)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleDistrictChange = option => {
    if (option) {
      if (option.value === 'all') {
        setAllDistricts()
      } else {
        setDistrict(option.value)
      }
    } else {
      if (isViewingAllDistricts()) {
        setAllDistricts()
      } else {
        reset()
      }
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  const handleSubcountyChange = option => {
    if (option && selectedDistrictData) {
      setSubcounty(option.value)
    } else {
      setDistrict(selectedDistrict)
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  const handleParishChange = option => {
    if (option && selectedSubcountyData) {
      setParish(option.value)
    } else {
      setSubcounty(selectedSubcounty)
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  const handleVillageChange = option => {
    if (option && selectedParishData) {
      setVillage(option.value)
    } else {
      setParish(selectedParish)
    }

    // Scroll to main content area (just below the main selector panel)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 280, behavior: 'smooth' })
      })
    })
  }

  // Generate district options with "All Districts" for multi-district mode
  const getDistrictOptions = () => {
    const options = availableDistricts.map(d => ({
      value: d.id,
      label: d.name,
    }))
    if (!isSingleDistrictMode) {
      options.unshift({ value: 'all', label: 'All Districts' })
    }
    return options
  }

  const districtOptions = getDistrictOptions()
  const subcountyOptions = availableSubcounties.map(s => ({
    value: s.id,
    label: s.name,
  }))
  const parishOptions = availableParishes.map(p => ({
    value: p.id,
    label: p.name,
  }))
  const villageOptions = availableVillages.map(v => ({
    value: v.id,
    label: v.name,
  }))

  if (!isVisible) return null

  const getCurrentDistrictValue = () => {
    if (isViewingAllDistricts()) return 'all'
    return selectedDistrict
  }

  return (
    <div className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-surface-200 shadow-sm z-40">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-semibold text-surface-700 whitespace-nowrap">
            📍 Location:
          </span>

          <div className="flex items-center space-x-4 flex-1 min-w-0">
            {isSingleDistrictMode ? (
              /* Single District Mode: Show district name as label */
              <div className="px-3 py-2 bg-primary-50 border border-primary-200 rounded-md">
                <span className="text-sm font-medium text-primary-800">
                  {focusedDistrict?.split('-')[1].charAt(0).toUpperCase() +
                    focusedDistrict?.split('-')[1].slice(1)}{' '}
                  District
                </span>
              </div>
            ) : (
              /* Multi District Mode: Show district selector */
              getLocationDepthOrder().includes('district') && (
                <div className="w-56">
                  <Select
                    value={
                      districtOptions.find(
                        d => d.value === getCurrentDistrictValue()
                      ) || null
                    }
                    onChange={handleDistrictChange}
                    options={districtOptions}
                    placeholder="District"
                    isClearable
                    styles={customSelectStyles}
                    className="text-sm"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              )
            )}

            {getLocationDepthOrder().includes('subcounty') && (
              <div className="w-56">
                <Select
                  value={
                    subcountyOptions.find(s => s.value === selectedSubcounty) ||
                    null
                  }
                  onChange={handleSubcountyChange}
                  options={subcountyOptions}
                  placeholder="Subcounty"
                  isClearable
                  isDisabled={!selectedDistrict || isViewingAllDistricts()}
                  styles={customSelectStyles}
                  className="text-sm"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}

            {getLocationDepthOrder().includes('parish') && (
              <div className="w-56">
                <Select
                  value={
                    parishOptions.find(p => p.value === selectedParish) || null
                  }
                  onChange={handleParishChange}
                  options={parishOptions}
                  placeholder="Parish"
                  isClearable
                  isDisabled={!selectedSubcounty || isViewingAllDistricts()}
                  styles={customSelectStyles}
                  className="text-sm"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}

            {getLocationDepthOrder().includes('village') && (
              <div className="w-56">
                <Select
                  value={
                    villageOptions.find(v => v.value === selectedVillage) ||
                    null
                  }
                  onChange={handleVillageChange}
                  options={villageOptions}
                  placeholder="Village"
                  isClearable
                  isDisabled={!selectedParish || isViewingAllDistricts()}
                  styles={customSelectStyles}
                  className="text-sm"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}
          </div>

          {(selectedDistrict ||
            selectedSubcounty ||
            selectedParish ||
            selectedVillage) && (
            <button
              onClick={() => {
                reset()
                // Scroll to main content area (just below the main selector panel)
                window.requestAnimationFrame(() => {
                  window.requestAnimationFrame(() => {
                    window.scrollTo({ top: 280, behavior: 'smooth' })
                  })
                })
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
            >
              Clear All
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default StickyLocationSelector
